/**
 * Enhanced Voice-to-Form Service
 * Intelligent form pre-filling with ML, context awareness, and learning
 * Replaces basic voice parsing with smart field mapping
 */

import { tenantLearningEngine } from './TenantLearningEngine';
import { contextManager } from './ContextManager';
import { openAIService } from './openaiService';
import { getCompanyId } from '../tenantContext';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase/client';

export interface FormFillResult {
  success: boolean;
  fields: Record<string, FieldValue>;
  confidence: number;
  needsConfirmation: boolean;
  suggestions?: Record<string, any[]>; // Alternative values
  ambiguities?: string[]; // Fields that need clarification
  smartDefaults?: Record<string, any>; // Pre-filled based on history
}

export interface FieldValue {
  value: any;
  confidence: number;
  source: 'voice' | 'learned' | 'history' | 'default' | 'predicted';
  alternativeValues?: any[];
}

export interface FieldMapping {
  voicePatterns: RegExp[]; // Regex patterns to match in transcript
  fieldName: string;
  valueExtractor: (match: RegExpMatchArray) => any;
  validator?: (value: any) => boolean;
  unit?: string;
}

export interface SmartDefault {
  fieldName: string;
  value: any;
  confidence: number;
  reason: string;
}

class EnhancedVoiceToForm {
  private static instance: EnhancedVoiceToForm;
  
  // Enhanced field mappings with multiple patterns
  private fieldMappings: FieldMapping[] = [
    // Oil volume
    {
      voicePatterns: [
        /(\d+(?:\.\d+)?)\s*(?:barrels?|bbls?|bbl)?\s*(?:of\s+)?oil/i,
        /oil\s*(?:volume|production)?:?\s*(\d+(?:\.\d+)?)/i,
        /(\d+(?:\.\d+)?)\s*(?:on\s+the\s+)?oil\s*(?:side|tank)?/i
      ],
      fieldName: 'tank1Level',
      valueExtractor: (match) => parseFloat(match[1]),
      validator: (value) => value >= 0 && value <= 10000,
      unit: 'barrels'
    },
    // Water volume
    {
      voicePatterns: [
        /(\d+(?:\.\d+)?)\s*(?:barrels?|bbls?|bbl)?\s*(?:of\s+)?water/i,
        /water\s*(?:volume|production)?:?\s*(\d+(?:\.\d+)?)/i,
        /(\d+(?:\.\d+)?)\s*(?:on\s+the\s+)?water\s*(?:side|tank)?/i
      ],
      fieldName: 'tank2Level',
      valueExtractor: (match) => parseFloat(match[1]),
      validator: (value) => value >= 0 && value <= 10000,
      unit: 'barrels'
    },
    // Gas volume
    {
      voicePatterns: [
        /(\d+(?:\.\d+)?)\s*(?:mcf|thousand\s+cubic\s+feet)\s*(?:of\s+)?gas/i,
        /gas\s*(?:volume|production)?:?\s*(\d+(?:\.\d+)?)/i,
        /(\d+(?:\.\d+)?)\s*gas/i
      ],
      fieldName: 'gasVolume',
      valueExtractor: (match) => parseFloat(match[1]),
      validator: (value) => value >= 0 && value <= 100000,
      unit: 'mcf'
    },
    // Tubing pressure
    {
      voicePatterns: [
        /tubing\s*(?:pressure)?:?\s*(\d+)/i,
        /(\d+)\s*(?:psi)?\s*tubing/i,
        /tbg\s*(?:pressure)?:?\s*(\d+)/i
      ],
      fieldName: 'tubingPressure',
      valueExtractor: (match) => parseInt(match[1]),
      validator: (value) => value >= 0 && value <= 5000,
      unit: 'psi'
    },
    // Casing pressure
    {
      voicePatterns: [
        /casing\s*(?:pressure)?:?\s*(\d+)/i,
        /(\d+)\s*(?:psi)?\s*casing/i,
        /csg\s*(?:pressure)?:?\s*(\d+)/i
      ],
      fieldName: 'casingPressure',
      valueExtractor: (match) => parseInt(match[1]),
      validator: (value) => value >= 0 && value <= 5000,
      unit: 'psi'
    },
    // Line pressure
    {
      voicePatterns: [
        /(?:flow\s*)?line\s*(?:pressure)?:?\s*(\d+)/i,
        /(\d+)\s*(?:psi)?\s*(?:flow\s*)?line/i
      ],
      fieldName: 'linePressure',
      valueExtractor: (match) => parseInt(match[1]),
      validator: (value) => value >= 0 && value <= 2000,
      unit: 'psi'
    },
    // Pump status
    {
      voicePatterns: [
        /pump\s*(?:is\s*)?(running|down|off|stopped|pumping)/i,
        /(running|down|off|stopped)\s*pump/i
      ],
      fieldName: 'pumpStatus',
      valueExtractor: (match) => {
        const status = match[1].toLowerCase();
        if (status === 'running' || status === 'pumping') return 'running';
        if (status === 'down' || status === 'stopped' || status === 'off') return 'down';
        return status;
      }
    },
    // Stroke rate (SPM)
    {
      voicePatterns: [
        /(\d+)\s*(?:strokes?)?\s*(?:per\s+)?(?:minute|spm)/i,
        /(?:stroke\s+)?(?:rate|speed):?\s*(\d+)/i
      ],
      fieldName: 'strokeRate',
      valueExtractor: (match) => parseInt(match[1]),
      validator: (value) => value >= 0 && value <= 30,
      unit: 'SPM'
    },
    // Temperature
    {
      voicePatterns: [
        /(?:temp|temperature):?\s*(\d+)/i,
        /(\d+)\s*degrees?/i
      ],
      fieldName: 'temperature',
      valueExtractor: (match) => parseInt(match[1]),
      validator: (value) => value >= -50 && value <= 200,
      unit: '°F'
    }
  ];

  private constructor() {}

  static getInstance(): EnhancedVoiceToForm {
    if (!EnhancedVoiceToForm.instance) {
      EnhancedVoiceToForm.instance = new EnhancedVoiceToForm();
    }
    return EnhancedVoiceToForm.instance;
  }

  /**
   * Parse voice transcript and fill form fields with ML enhancement
   */
  async parseAndFill(
    transcript: string,
    formType: string,
    wellId?: string,
    companyId?: string
  ): Promise<FormFillResult> {
    const tenant = companyId || getCompanyId();
    const context = contextManager.getContext();
    
    try {
      // 1. Basic pattern matching
      const basicFields = this.extractFieldsFromTranscript(transcript);

      // 2. Use GPT-4 for complex/ambiguous transcripts
      const enhancedFields = await this.enhanceWithGPT4(transcript, basicFields, formType);

      // 3. Apply tenant-specific learning
      const learnedFields = await this.applyTenantLearning(
        transcript,
        enhancedFields,
        tenant
      );

      // 4. Add smart defaults from history
      const smartDefaults = await this.getSmartDefaults(
        wellId || context.selectedWell?.id,
        tenant,
        learnedFields
      );

      // 5. Merge all field sources
      const mergedFields = this.mergeFields(learnedFields, smartDefaults);

      // 6. Calculate overall confidence
      const confidence = this.calculateOverallConfidence(mergedFields);

      // 7. Identify ambiguities
      const ambiguities = this.identifyAmbiguities(mergedFields);

      // 8. Generate suggestions for low-confidence fields
      const suggestions = await this.generateSuggestions(mergedFields, wellId, tenant);

      return {
        success: Object.keys(mergedFields).length > 0,
        fields: mergedFields,
        confidence,
        needsConfirmation: confidence < 0.8 || ambiguities.length > 0,
        ambiguities,
        suggestions,
        smartDefaults: Object.fromEntries(
          Object.entries(mergedFields)
            .filter(([_, v]) => v.source === 'history' || v.source === 'predicted')
            .map(([k, v]) => [k, v.value])
        )
      };

    } catch (error) {
      console.error('Voice-to-form parsing failed:', error);
      return {
        success: false,
        fields: {},
        confidence: 0,
        needsConfirmation: true,
        ambiguities: ['Failed to parse transcript']
      };
    }
  }

  /**
   * Extract fields using pattern matching
   */
  private extractFieldsFromTranscript(transcript: string): Record<string, FieldValue> {
    const fields: Record<string, FieldValue> = {};

    for (const mapping of this.fieldMappings) {
      for (const pattern of mapping.voicePatterns) {
        const match = transcript.match(pattern);
        if (match) {
          try {
            const value = mapping.valueExtractor(match);
            
            // Validate if validator exists
            if (!mapping.validator || mapping.validator(value)) {
              fields[mapping.fieldName] = {
                value,
                confidence: 0.9,
                source: 'voice'
              };
              break; // Found match, move to next mapping
            }
          } catch (error) {
          }
        }
      }
    }

    return fields;
  }

  /**
   * Enhance fields using GPT-4 for complex cases
   */
  private async enhanceWithGPT4(
    transcript: string,
    basicFields: Record<string, FieldValue>,
    formType: string
  ): Promise<Record<string, FieldValue>> {
    // If we got good results from pattern matching, skip GPT-4
    if (Object.keys(basicFields).length >= 3) {
      return basicFields;
    }

    try {
      const prompt = `Extract production data from this transcript:

"${transcript}"

Form type: ${formType}

Extract these fields if mentioned:
- Oil volume (barrels)
- Water volume (barrels)
- Gas volume (mcf)
- Tubing pressure (psi)
- Casing pressure (psi)
- Line pressure (psi)
- Pump status (running/down)
- Stroke rate (SPM)
- Temperature (°F)

Return JSON with field names and values. Use null for fields not mentioned.
Example: { "tank1Level": 25.5, "tank2Level": 15, "tubingPressure": 350 }`;

      const response = await openAIService.chat(prompt);
      const gptFields = JSON.parse(response.response);

      // Merge with basic fields (basic fields take precedence)
      const enhanced: Record<string, FieldValue> = { ...basicFields };

      for (const [fieldName, value] of Object.entries(gptFields)) {
        if (value !== null && !enhanced[fieldName]) {
          enhanced[fieldName] = {
            value,
            confidence: 0.75, // Lower confidence for GPT extraction
            source: 'voice'
          };
        }
      }

      return enhanced;

    } catch (error) {
      console.error('GPT-4 enhancement failed:', error);
      return basicFields;
    }
  }

  /**
   * Apply tenant-specific vocabulary learning
   */
  private async applyTenantLearning(
    transcript: string,
    fields: Record<string, FieldValue>,
    companyId: string
  ): Promise<Record<string, FieldValue>> {
    try {
      const learningData = await tenantLearningEngine.getTenantData(companyId);
      
      // Apply custom vocabulary
      let normalizedTranscript = transcript.toLowerCase();
      for (const [key, value] of Object.entries(learningData.customVocabulary)) {
        normalizedTranscript = normalizedTranscript.replace(
          new RegExp(key, 'gi'),
          value
        );
      }

      // If transcript changed, re-extract fields
      if (normalizedTranscript !== transcript.toLowerCase()) {
        const reExtracted = this.extractFieldsFromTranscript(normalizedTranscript);
        
        // Merge learned fields
        for (const [fieldName, value] of Object.entries(reExtracted)) {
          if (!fields[fieldName]) {
            fields[fieldName] = {
              ...value,
              source: 'learned',
              confidence: value.confidence * 0.95
            };
          }
        }
      }

    } catch (error) {
    }

    return fields;
  }

  /**
   * Get smart defaults from production history
   */
  private async getSmartDefaults(
    wellId: string | undefined,
    companyId: string,
    extractedFields: Record<string, FieldValue>
  ): Promise<SmartDefault[]> {
    const defaults: SmartDefault[] = [];

    if (!wellId) return defaults;

    try {
      // Get last 7 entries for this well
      const entriesRef = collection(db, `companies/${companyId}/wells/${wellId}/production_entries`);
      const recentQuery = query(
        entriesRef,
        orderBy('entryDate', 'desc'),
        limit(7)
      );

      const snapshot = await getDocs(recentQuery);
      if (snapshot.empty) return defaults;

      const entries = snapshot.docs.map(doc => doc.data());

      // Calculate averages/patterns for missing fields
      const numericFields = ['tank1Level', 'tank2Level', 'gasVolume', 'tubingPressure', 'casingPressure'];

      for (const fieldName of numericFields) {
        if (extractedFields[fieldName]) continue; // Already extracted

        const values = entries
          .map(e => e[fieldName])
          .filter(v => v != null && typeof v === 'number');

        if (values.length >= 3) {
          // Use median for more stable prediction
          const sorted = [...values].sort((a, b) => a - b);
          const median = sorted[Math.floor(sorted.length / 2)];

          defaults.push({
            fieldName,
            value: median,
            confidence: 0.7,
            reason: `Based on recent average (${values.length} entries)`
          });
        }
      }

      // Pump status pattern (most common in last 7)
      if (!extractedFields['pumpStatus']) {
        const statuses = entries.map(e => e.pumpStatus).filter(Boolean);
        if (statuses.length > 0) {
          const mostCommon = this.getMostCommon(statuses);
          defaults.push({
            fieldName: 'pumpStatus',
            value: mostCommon,
            confidence: 0.6,
            reason: 'Most common recent status'
          });
        }
      }

    } catch (error) {
      console.error('Failed to get smart defaults:', error);
    }

    return defaults;
  }

  /**
   * Merge fields from different sources
   */
  private mergeFields(
    extractedFields: Record<string, FieldValue>,
    smartDefaults: SmartDefault[]
  ): Record<string, FieldValue> {
    const merged: Record<string, FieldValue> = { ...extractedFields };

    // Add smart defaults for missing fields
    for (const defaultVal of smartDefaults) {
      if (!merged[defaultVal.fieldName]) {
        merged[defaultVal.fieldName] = {
          value: defaultVal.value,
          confidence: defaultVal.confidence,
          source: 'history'
        };
      }
    }

    return merged;
  }

  /**
   * Calculate overall confidence score
   */
  private calculateOverallConfidence(fields: Record<string, FieldValue>): number {
    if (Object.keys(fields).length === 0) return 0;

    const confidences = Object.values(fields).map(f => f.confidence);
    return confidences.reduce((sum, c) => sum + c, 0) / confidences.length;
  }

  /**
   * Identify ambiguous fields that need confirmation
   */
  private identifyAmbiguities(fields: Record<string, FieldValue>): string[] {
    const ambiguities: string[] = [];

    for (const [fieldName, value] of Object.entries(fields)) {
      if (value.confidence < 0.7) {
        ambiguities.push(fieldName);
      }
    }

    return ambiguities;
  }

  /**
   * Generate alternative suggestions for fields
   */
  private async generateSuggestions(
    fields: Record<string, FieldValue>,
    wellId: string | undefined,
    companyId: string
  ): Promise<Record<string, any[]>> {
    const suggestions: Record<string, any[]> = {};

    // For ambiguous numeric fields, suggest nearby values
    for (const [fieldName, value] of Object.entries(fields)) {
      if (value.confidence < 0.8 && typeof value.value === 'number') {
        const base = value.value;
        suggestions[fieldName] = [
          base,
          Math.round(base * 0.9),
          Math.round(base * 1.1)
        ];
      }
    }

    return suggestions;
  }

  /**
   * Get most common value in array
   */
  private getMostCommon<T>(arr: T[]): T {
    const counts = arr.reduce((acc, val) => {
      acc[String(val)] = (acc[String(val)] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return arr.sort((a, b) => counts[String(b)] - counts[String(a)])[0];
  }

  /**
   * Register custom field mapping for tenant
   */
  registerCustomMapping(mapping: FieldMapping): void {
    this.fieldMappings.push(mapping);
  }
}

// Export singleton
export const enhancedVoiceToForm = EnhancedVoiceToForm.getInstance();
